import React, { useState, useEffect, useMemo } from 'react';
import Modal from '../common/Modal';
import ModalContent from '../common/ModalContent';
import { Button } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { FormField, Select } from '../common/Form';
import { getGenderTextColor } from '../../utils/genderColors';
import { formatPlayerName } from '../../utils/nameUtils';
import { formatTournamentEntryLabel } from '../../utils/tournamentFormat';

const formatTeamLabel = (team) => {
  if (!team?.player1) return 'Unknown';
  if (!team.player2) return formatTournamentEntryLabel(team, true);
  const p1 = formatPlayerName(team.player1);
  const p2 = formatPlayerName(team.player2);
  return `${p1} & ${p2}`;
};

/**
 * Modal for host to edit knockout match pairings (who plays who)
 */
const EditKnockoutMatchupModal = ({
  isOpen,
  onClose,
  match,
  teams,
  onSave,
  saving = false
}) => {
  const [team1Id, setTeam1Id] = useState('');
  const [team2Id, setTeam2Id] = useState('');
  const [error, setError] = useState(null);

  const teamOptions = useMemo(() => {
    const opts = [{ value: '', label: 'TBD (Winner to be determined)' }];
    (teams || []).forEach((team) => {
      opts.push({
        value: team.id,
        label: formatTeamLabel(team)
      });
    });
    return opts;
  }, [teams]);

  useEffect(() => {
    if (isOpen && match) {
      setTeam1Id(match.entry1_id || '');
      setTeam2Id(match.entry2_id || '');
      setError(null);
    }
  }, [isOpen, match]);

  if (!isOpen || !match) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (team1Id && team2Id && team1Id === team2Id) {
      setError('Team 1 and Team 2 must be different');
      return;
    }

    try {
      await onSave({
        team1Id: team1Id || null,
        team2Id: team2Id || null
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update matchup');
    }
  };

  const roundLabels = {
    round_of_16: 'Round of 16',
    quarter_final: 'Quarter Final',
    semi_final: 'Semi Final',
    final: 'Final'
  };
  const roundLabel = roundLabels[match.round] || match.round;

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnBackdropClick={!saving}>
      <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden">
        <div className="p-6 border-b border-border-medium">
          <div className="flex items-center justify-between">
            <Heading level={2}>Edit Matchup</Heading>
            <button
              onClick={onClose}
              disabled={saving}
              className="text-text-muted hover:text-text-primary transition-colors disabled:opacity-50"
            >
              ✕
            </button>
          </div>
          <Text className="text-text-secondary mt-2 block">
            {roundLabel} – Change which teams play in this match.
          </Text>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 p-3 bg-danger/10 border border-danger rounded-lg">
              <Typography variant="body" className="text-danger">{error}</Typography>
            </div>
          )}

          <div className="space-y-4">
            <FormField label="Team 1">
              <Select
                value={team1Id}
                onChange={(e) => setTeam1Id(e.target.value)}
                options={teamOptions}
                placeholder="Select team"
              />
            </FormField>

            <div className="text-center text-text-muted font-medium">vs</div>

            <FormField label="Team 2">
              <Select
                value={team2Id}
                onChange={(e) => setTeam2Id(e.target.value)}
                options={teamOptions}
                placeholder="Select team"
              />
            </FormField>
          </div>

          <div className="flex gap-2 mt-6 pt-4 border-t border-border-medium">
            <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save Matchup'}
            </Button>
          </div>
        </form>
      </ModalContent>
    </Modal>
  );
};

export default EditKnockoutMatchupModal;
